<template>
  <div class="detail-view">
    <div class="detail-view-intro">
      <h1 class="detail-view-title">
        Detalhamento da importação · {{ agentName }}
      </h1>
    </div>

    <p v-if="loading" class="detail-view-loading">
      Carregando dados da importação…
    </p>

    <template v-else>
      <div class="detail-stats detail-stats--6col detail-stats--7">
        <StatTile
          label="Enviadas ao Phoenix"
          :value="totals.enviadas"
          icon="mdi-send-outline"
          color="secondary"
          :hint="`${totals.execucoesConferidas} de ${totals.execucoes} execução(ões) conferida(s)`"
        />
        <StatTile
          label="Importadas sem pendência"
          :value="totals.semPendencia"
          icon="mdi-check-circle-outline"
          color="success"
          :badge="`${pctDe(totals.semPendencia)}%`"
          hint="Entraram agora e não geraram pendência"
        />
        <StatTile
          label="Resolvidas via reimportação"
          :value="totals.resolvidasReimport"
          icon="mdi-file-sync-outline"
          color="success"
          :badge="`${pctDe(totals.resolvidasReimport)}%`"
          hint="Estavam em pendência e a reimportação as trouxe sem pendência"
        />
        <StatTile
          label="Com pendência (rastreadas)"
          :value="totals.pendenciasRastreadas"
          icon="mdi-clipboard-alert-outline"
          color="warning"
          :badge="`${pctDe(totals.pendenciasRastreadas)}%`"
          :hint="`${totals.emPendenciaAgora} em pendência agora · ${totals.finalizadasFicticias} concluídas com dados fictícios · ${totals.resolvidas} resolvidas`"
        />
        <StatTile
          label="Já existiam no Phoenix sem pendência"
          :value="totals.jaExistiam"
          icon="mdi-content-duplicate"
          color="secondary"
          :badge="`${pctDe(totals.jaExistiam)}%`"
          hint="Já estavam no Phoenix e não foram verificadas de novo"
        />
        <StatTile
          label="Erros de importação"
          :value="totals.erro"
          icon="mdi-alert-circle-outline"
          color="error"
          :badge="`${pctDe(totals.erro)}%`"
          :hint="
            totals.naoVerificadas
              ? `+ ${totals.naoVerificadas} não verificada(s)`
              : 'Enviadas que não entraram'
          "
        />
        <StatTile
          label="Constam canceladas no Phoenix"
          :value="totals.canceladas"
          icon="mdi-cancel"
          color="secondary"
          :hint="`${totals.canceladasNaoImportadas} não importadas · ${totals.canceladasComPendencia} em pendência`"
        />
      </div>

      <section class="detail-section">
        <div class="detail-section-title">Do envio ao Phoenix</div>
        <div v-if="totals.enviadas" class="funnel">
          <div class="funnel-bar">
            <div
              v-for="seg in segmentos"
              :key="seg.key"
              class="funnel-seg"
              :class="`funnel-seg--${seg.key}`"
              :style="{ width: pct(seg.value) }"
              :title="`${seg.label}: ${seg.value}`"
            >
              <span v-if="segLabelVisible(seg.value)">{{ seg.value }}</span>
            </div>
          </div>
          <div class="funnel-legend">
            <span
              v-for="seg in segmentos"
              :key="seg.key"
              class="funnel-legend-item"
            >
              <span class="dot" :class="`dot--${seg.key}`"></span>
              {{ seg.value }} {{ seg.label }} ({{ pctDe(seg.value) }}%)
            </span>
          </div>
          <p v-if="totals.execucoesSemFoto" class="funnel-note">
            {{ totals.execucoesSemFoto }} execução(ões) sem conferência prévia
            no Phoenix: nelas nenhuma reserva é contada como "já existia".
          </p>
        </div>
        <p v-else class="detail-empty">
          Nenhuma reserva foi enviada ao Phoenix neste período.
        </p>
      </section>

      <section class="detail-section">
        <div class="detail-section-title">Execuções no período</div>
        <div v-if="executions.length" class="detail-table-wrap">
          <table class="detail-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Janela</th>
                <th>Status</th>
                <th>Período importado</th>
                <th class="num">Enviadas</th>
                <th class="num">Já existiam sem pend.</th>
                <th class="num">Sem pendência</th>
                <th class="num">Resolvidas reimport.</th>
                <th class="num">Em pendência</th>
                <th class="num">Canceladas</th>
                <th class="num">Erros</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ex in executions" :key="ex.id">
                <td>{{ ex.date }}</td>
                <td class="muted">
                  {{ ex.start }}–{{ ex.end || "…" }} · {{ ex.duration }}
                </td>
                <td><StatusChip :status="ex.status" /></td>
                <td class="muted">{{ ex.periodo || "—" }}</td>
                <template v-if="ex.conferida">
                  <td class="num">{{ ex.enviadas }}</td>
                  <td class="num">{{ ex.jaExistiam }}</td>
                  <td class="num ok">{{ ex.semPendencia }}</td>
                  <td class="num ok">{{ ex.resolvidasReimport }}</td>
                  <td class="num">{{ ex.comPendencia }}</td>
                  <td class="num">{{ ex.canceladas }}</td>
                  <td class="num" :class="{ err: ex.erro > 0 }">
                    {{ ex.erro }}
                    <span
                      v-if="ex.naoVerificadas"
                      class="muted"
                      :title="`${ex.naoVerificadas} não verificada(s)`"
                    >
                      (+{{ ex.naoVerificadas }})
                    </span>
                  </td>
                </template>
                <td v-else class="muted" colspan="7">
                  {{ semNumeros(ex) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4">Total do período</td>
                <td class="num">{{ totals.enviadas }}</td>
                <td class="num">{{ totals.jaExistiam }}</td>
                <td class="num ok">{{ totals.semPendencia }}</td>
                <td class="num ok">{{ totals.resolvidasReimport }}</td>
                <td class="num">{{ totals.comPendencia }}</td>
                <td class="num">{{ totals.canceladasNaoImportadas }}</td>
                <td class="num" :class="{ err: totals.erro > 0 }">
                  {{ totals.erro }}
                  <span v-if="totals.naoVerificadas" class="muted"
                    >(+{{ totals.naoVerificadas }})</span
                  >
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p v-else class="detail-empty">Nenhuma execução neste período.</p>
      </section>

      <ListaRecolhivel
        titulo="Reservas com erro na importação"
        :total="errosMeta.total"
      >
        <div v-if="erros.length" class="detail-table-wrap">
          <table class="detail-table import-error-table">
            <colgroup>
              <col class="col-voucher" />
              <col class="col-motivo" />
              <col class="col-job" />
              <col class="col-detectada" />
            </colgroup>
            <thead>
              <tr>
                <th>Localizador</th>
                <th>Motivo</th>
                <th>Execução</th>
                <th>Base</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(e, i) in erros"
                :key="`${e.execId}-${e.localizador}-${i}`"
              >
                <td class="voucher-cell">{{ e.localizador }}</td>
                <td>{{ e.motivo }}</td>
                <td class="muted job-cell">
                  {{ e.execDate }} · {{ e.execStart }}
                </td>
                <td class="muted">{{ e.baseLabel }}</td>
              </tr>
            </tbody>
            <tfoot v-if="errosMeta.listadas < errosMeta.total">
              <tr>
                <td colspan="4">
                  Listando {{ errosMeta.listadas }} de {{ errosMeta.total }} no
                  período.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="detail-empty detail-empty--ok">
          <v-icon size="18" color="#0f7a6e">mdi-check-circle-outline</v-icon>
          Nenhuma reserva.
        </div>
      </ListaRecolhivel>

      <ListaRecolhivel
        titulo="Resolvidas via reimportação"
        :total="resolvidasReimportMeta.total"
      >
        <p class="funnel-note">
          Estavam em pendência no Phoenix antes da importação e a reimportação
          as trouxe sem pendência (o Phoenix apaga e recria a pendente; o motivo
          já tinha sido corrigido, por exemplo o cadastro de um voo).
        </p>
        <div v-if="resolvidasReimport.length" class="detail-table-wrap">
          <table class="detail-table alerta-table">
            <thead>
              <tr>
                <th>Reserva / Localizador</th>
                <th>Cliente</th>
                <th>Chegada</th>
                <th>Pendência que tinha</th>
                <th>Detectada</th>
                <th>Resolvida na execução</th>
                <th>Base</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(i, k) in resolvidasReimport"
                :key="`${i.execId}-${i.localizador}-${k}`"
              >
                <td>
                  <strong>{{ i.localizador }}</strong>
                  <span v-if="i.codigo" class="muted"> · {{ i.codigo }}</span>
                </td>
                <td class="ellipsis">{{ i.cliente || "—" }}</td>
                <td>{{ i.dataChegada || "—" }}</td>
                <td>{{ i.motivoOriginal || "—" }}</td>
                <td class="muted">{{ i.detectadaEm || "—" }}</td>
                <td class="muted job-cell">
                  {{ i.execDate }} · {{ i.execStart }}
                </td>
                <td class="muted">{{ i.baseLabel }}</td>
              </tr>
            </tbody>
            <tfoot
              v-if="resolvidasReimportMeta.listadas < resolvidasReimportMeta.total"
            >
              <tr>
                <td colspan="7">
                  Listando {{ resolvidasReimportMeta.listadas }} de
                  {{ resolvidasReimportMeta.total }} no período.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="detail-empty detail-empty--ok">
          <v-icon size="18" color="#0f7a6e">mdi-check-circle-outline</v-icon>
          Nenhuma pendência foi resolvida por reimportação neste período.
        </div>
      </ListaRecolhivel>

      <ListaRecolhivel
        titulo="Reservas importadas sem pendência"
        :total="semPendenciaMeta.total"
      >
        <div v-if="semPendencia.length" class="detail-table-wrap">
          <table class="detail-table import-error-table">
            <colgroup>
              <col class="col-voucher" />
              <col class="col-motivo" />
              <col class="col-job" />
              <col class="col-detectada" />
            </colgroup>
            <thead>
              <tr>
                <th>Localizador</th>
                <th>Resultado</th>
                <th>Execução</th>
                <th>Base</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(i, k) in semPendencia"
                :key="`${i.execId}-${i.localizador}-${k}`"
              >
                <td class="voucher-cell">{{ i.localizador }}</td>
                <td>Entrou no Phoenix sem pendência</td>
                <td class="muted job-cell">
                  {{ i.execDate }} · {{ i.execStart }}
                </td>
                <td class="muted">{{ i.baseLabel }}</td>
              </tr>
            </tbody>
            <tfoot v-if="semPendenciaMeta.listadas < semPendenciaMeta.total">
              <tr>
                <td colspan="4">
                  Listando {{ semPendenciaMeta.listadas }} de
                  {{ semPendenciaMeta.total }} no período.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="detail-empty detail-empty--ok">
          <v-icon size="18" color="#0f7a6e">mdi-check-circle-outline</v-icon>
          Nenhuma reserva.
        </div>
      </ListaRecolhivel>

      <ListaRecolhivel
        v-for="s in secoesPend"
        :key="s.key"
        :titulo="s.titulo"
        :total="s.meta.total"
      >
        <div v-if="s.lista.length" class="detail-table-wrap">
          <table class="detail-table import-error-table import-pend-table">
            <colgroup>
              <col class="col-voucher" />
              <col class="col-status" />
              <col class="col-motivo" />
              <col class="col-job" />
            </colgroup>
            <thead>
              <tr>
                <th>Reserva / Localizador</th>
                <th>Situação agora</th>
                <th>Motivo</th>
                <th>Execução</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(p, k) in s.lista"
                :key="`${s.key}-${p.execId}-${p.codigo}-${k}`"
              >
                <td class="voucher-cell">
                  {{ p.codigo }}
                  <span v-if="p.localizador" class="muted">
                    · {{ p.localizador }}</span
                  >
                  <span v-if="p.operacao" class="muted">
                    · {{ p.operacao }}</span
                  >
                </td>
                <td>
                  <span class="rate-pill" :class="situacaoClasse(p)">{{
                    situacaoLabel(p)
                  }}</span>
                </td>
                <td>{{ p.motivo || motivoPadrao(p) }}</td>
                <td class="muted job-cell">
                  {{ p.execDate }} · {{ p.execStart }}
                </td>
              </tr>
            </tbody>
            <tfoot v-if="s.meta.listadas < s.meta.total">
              <tr>
                <td colspan="4">
                  Listando {{ s.meta.listadas }} de {{ s.meta.total }} no
                  período.
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else class="detail-empty detail-empty--ok">
          <v-icon size="18" color="#0f7a6e">mdi-check-circle-outline</v-icon>
          {{ s.vazio }}
        </div>
      </ListaRecolhivel>

      <PlanilhaAlertas
        :mesmo-cpf="alertas.mesmoCpf"
        :fora-da-planilha="alertas.foraDaPlanilha"
        :verificado="alertas.verificado"
        :apelidos-criados="alertas.apelidosCriados"
      />

      <ListaRecolhivel
        titulo="Reservas alteradas na planilha"
        :total="alteracoes.length"
      >
        <PlanilhaAlteracoes
          :alteracoes="alteracoes"
          :meta="{ total: alteracoes.length, listadas: alteracoes.length }"
          tipo="editada"
        />
      </ListaRecolhivel>
      <ListaRecolhivel v-if="base === 'natal'" titulo="Troca de serviço (Natal)" :total="trocasServico.length">
        <PlanilhaTrocaServico :trocas="trocasServico" />
      </ListaRecolhivel>

      <ListaRecolhivel
        titulo="Reservas canceladas no Phoenix"
        :total="canceladasMeta.total"
      >
        <div v-if="canceladas.length" class="detail-table-wrap">
          <table class="detail-table import-error-table import-pend-table">
            <colgroup>
              <col class="col-voucher" />
              <col class="col-status" />
              <col class="col-motivo" />
              <col class="col-job" />
            </colgroup>
            <thead>
              <tr>
                <th>Reserva / Localizador</th>
                <th>Situação agora</th>
                <th>Situação no Phoenix</th>
                <th>Execução</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(p, k) in canceladas"
                :key="`${p.base}-${p.codigo}-${k}`"
              >
                <td class="voucher-cell">
                  <template v-if="p.tipo === 'nao_importada'">{{
                    p.localizador
                  }}</template>
                  <template v-else>
                    {{ p.codigo }}
                    <span v-if="p.localizador" class="muted">
                      · {{ p.localizador }}</span
                    >
                  </template>
                </td>
                <td>
                  <span class="rate-pill" :class="situacaoClasse(p)">{{
                    situacaoLabel(p)
                  }}</span>
                </td>
                <td>
                  <template v-if="p.tipo === 'nao_importada'">
                    Não importada (já cancelada no Phoenix)
                  </template>
                  <template v-else>
                    Importada em pendência
                    <div
                      v-if="
                        p.canceladasNoPhoenix && p.canceladasNoPhoenix.length
                      "
                      class="canc-ref"
                    >
                      Cancelada:
                      <strong>{{
                        p.canceladasNoPhoenix.map((c) => c.code).join(", ")
                      }}</strong>
                      <span
                        v-if="p.canceladasNoPhoenix[0].atualizadaEm"
                        class="muted"
                      >
                        (cancelada em
                        {{
                          dataCurta(p.canceladasNoPhoenix[0].atualizadaEm)
                        }})</span
                      >
                    </div>
                    <div
                      v-if="p.resolvidaPelaRegra"
                      class="canc-ref canc-ref--ok"
                    >
                      Concluída pelo agente.
                    </div>
                  </template>
                </td>
                <td class="muted job-cell">
                  {{ p.execDate }} · {{ p.execStart }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="detail-empty detail-empty--ok">
          <v-icon size="18" color="#0f7a6e">mdi-check-circle-outline</v-icon>
          Nenhuma reserva.
        </div>
      </ListaRecolhivel>
    </template>
  </div>
</template>

<script>
import { getAzulImportDetail } from "@/services/azulApi.js";
import StatTile from "@/components/StatTile/StatTile.vue";
import StatusChip from "@/components/StatusChip/StatusChip.vue";
import PlanilhaAlertas from "./PlanilhaAlertas.vue";
import PlanilhaAlteracoes from "./PlanilhaAlteracoes.vue";
import PlanilhaTrocaServico from "./PlanilhaTrocaServico.vue";
import ListaRecolhivel from "./ListaRecolhivel.vue";

const EMPTY_TOTALS = {
  execucoes: 0,
  execucoesConferidas: 0,
  execucoesSemFoto: 0,
  enviadas: 0,
  jaExistiam: 0,
  semPendencia: 0,
  resolvidasReimport: 0,
  comPendencia: 0,
  comPendenciaAindaPendentes: 0,
  pendenciasRastreadas: 0,
  emPendenciaAgora: 0,
  finalizadasFicticias: 0,
  resolvidas: 0,
  canceladas: 0,
  canceladasNaoImportadas: 0,
  canceladasComPendencia: 0,
  erro: 0,
  naoVerificadas: 0,
};
const META = { total: 0, listadas: 0 };

export default {
  name: "AzulImportDetail",
  components: { StatTile, StatusChip, PlanilhaAlertas, PlanilhaAlteracoes, PlanilhaTrocaServico, ListaRecolhivel },
  props: {
    range: { type: Object, required: true },
    agentName: { type: String, default: "Azul" },
    base: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      totals: EMPTY_TOTALS,
      executions: [],
      erros: [],
      errosMeta: META,
      semPendencia: [],
      semPendenciaMeta: META,
      resolvidasReimport: [],
      resolvidasReimportMeta: META,
      comPendencia: [],
      comPendenciaMeta: META,
      emPendencia: [],
      emPendenciaMeta: META,
      finalizadas: [],
      finalizadasMeta: META,
      resolvidas: [],
      resolvidasMeta: META,
      canceladas: [],
      canceladasMeta: META,
      alertas: {
        mesmoCpf: [],
        foraDaPlanilha: [],
        verificado: {},
        apelidosCriados: [],
      },
      alteracoes: [],
      trocasServico: [],
    };
  },
  computed: {
    // Pendências que o agente rastreia, por situação ATUAL. "Concluída com dados fictícios" é pendência, não resolvida.
    secoesPend() {
      return [
        {
          key: "em",
          titulo: "Reservas em pendência",
          // as que continuam em pendência + as que o agente concluiu com dado fictício (a etiqueta da linha diferencia)
          lista: [...this.emPendencia, ...this.finalizadas],
          meta: {
            total:
              (this.emPendenciaMeta.total || 0) +
              (this.finalizadasMeta.total || 0),
            listadas:
              (this.emPendenciaMeta.listadas || 0) +
              (this.finalizadasMeta.listadas || 0),
          },
          vazio: "Nenhuma reserva.",
        },
        {
          key: "res",
          titulo: "Reservas resolvidas",
          lista: this.resolvidas,
          meta: this.resolvidasMeta,
          vazio: "Nenhuma reserva.",
        },
      ];
    },
    // Cada reserva enviada cai em UMA categoria: a barra fecha em 100%.
    segmentos() {
      const t = this.totals;
      return [
        {
          key: "exist",
          label: "já existiam sem pendência",
          value: t.jaExistiam,
        },
        { key: "ok", label: "sem pendência", value: t.semPendencia },
        {
          key: "reimp",
          label: "resolvidas via reimportação",
          value: t.resolvidasReimport,
        },
        {
          key: "pend",
          label: "entraram com pendência (nesta execução)",
          value: t.comPendencia,
        },
        {
          key: "canc",
          label: "constam canceladas (não importadas)",
          value: t.canceladasNaoImportadas,
        },
        { key: "err", label: "com erro", value: t.erro },
        { key: "nv", label: "não verificadas", value: t.naoVerificadas },
      ].filter((s) => s.value > 0);
    },
  },
  watch: {
    range() {
      this.load();
    },
    base() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const d = await getAzulImportDetail(this.range, this.base);
        this.totals = { ...EMPTY_TOTALS, ...d.totals };
        this.executions = d.executions || [];
        this.erros = d.erros || [];
        this.errosMeta = d.errosMeta || META;
        this.semPendencia = d.semPendencia || [];
        this.semPendenciaMeta = d.semPendenciaMeta || META;
        this.resolvidasReimport = d.resolvidasReimport || [];
        this.resolvidasReimportMeta = d.resolvidasReimportMeta || META;
        this.comPendencia = d.comPendencia || [];
        this.comPendenciaMeta = d.comPendenciaMeta || META;
        this.emPendencia = d.emPendencia || [];
        this.emPendenciaMeta = d.emPendenciaMeta || META;
        this.finalizadas = d.finalizadas || [];
        this.finalizadasMeta = d.finalizadasMeta || META;
        this.resolvidas = d.resolvidas || [];
        this.resolvidasMeta = d.resolvidasMeta || META;
        this.canceladas = d.canceladas || [];
        this.canceladasMeta = d.canceladasMeta || META;
        this.alertas = {
          mesmoCpf: (d.alertas && d.alertas.mesmoCpf) || [],
          foraDaPlanilha: (d.alertas && d.alertas.foraDaPlanilha) || [],
          verificado: (d.alertas && d.alertas.verificado) || {},
          apelidosCriados: (d.alertas && d.alertas.apelidosCriados) || [],
        };
        this.alteracoes = d.alteracoes || [];
        this.trocasServico = d.trocasServico || [];
      } catch (err) {
        console.error("Falha ao carregar a importação da Azul", err);
        this.totals = EMPTY_TOTALS;
        this.executions = [];
        this.erros = [];
        this.errosMeta = META;
        this.semPendencia = [];
        this.semPendenciaMeta = META;
        this.comPendencia = [];
        this.comPendenciaMeta = META;
        this.emPendencia = [];
        this.emPendenciaMeta = META;
        this.finalizadas = [];
        this.finalizadasMeta = META;
        this.resolvidas = [];
        this.resolvidasMeta = META;
        this.canceladas = [];
        this.alertas = { mesmoCpf: [], foraDaPlanilha: [], verificado: {} };
        this.alteracoes = [];
        this.trocasServico = [];
        this.canceladasMeta = META;
      } finally {
        this.loading = false;
      }
    },
    pctDe(n) {
      return this.totals.enviadas
        ? Math.round((n / this.totals.enviadas) * 100)
        : 0;
    },
    pct(n) {
      return this.totals.enviadas
        ? `${(n / this.totals.enviadas) * 100}%`
        : "0%";
    },
    segLabelVisible(n) {
      return this.totals.enviadas && n / this.totals.enviadas > 0.06;
    },
    // Execucao sem numeros por reserva: explica por que, em vez de mostrar zeros inventados.
    semNumeros(ex) {
      if (ex.phoenixStatus === "pulado")
        return "Nada foi enviado ao Phoenix (planilha já importada)";
      if (ex.status === "andamento") return "Em andamento";
      if (ex.status === "erro") return "Terminou com erro antes de importar";
      if (ex.status === "sem_email" || ex.status === "aguardando_decisao")
        return "Sem planilha para importar";
      return "Sem conferência das reservas";
    },
    dataCurta(iso) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
      return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
    },
    regraTexto(r) {
      const nomes = {
        reserva_cancelada: "Reserva cancelada",
        hoteis: "Mais de um hotel",
        grupos: "Serviços unificados",
        voo_web: "Voo pela planilha",
        horario_voo: "Horário do voo divergente",
        veiculo: "Tipo de veículo definido",
        data_out: "Data do OUT ajustada (IN e OUT no mesmo dia)",
        apelido_hotel: "Apelido de hotel",
      };
      return String(r || "")
        .split("+")
        .map((x) => nomes[x] || x)
        .join(" + ");
    },
    situacaoLabel(p) {
      if (p.tipo === "nao_importada") return "Não importada";
      if (p.resolvidaPelaRegra) return "Resolvida pelo agente";
      if (p.status === "finalizada") return "Concluída com dado fictício";
      if (p.status === "resolvida") return "Resolvida";
      return p.tratamento === "manual" ? "Correção manual" : "Fila do agente";
    },
    situacaoClasse(p) {
      if (p.tipo === "nao_importada") return "rate-pill--warn";
      if (p.resolvidaPelaRegra) return "rate-pill--ok";
      if (p.status === "finalizada") return "rate-pill--warn";
      if (p.status === "resolvida") return "rate-pill--ok";
      return p.tratamento === "manual" ? "rate-pill--bad" : "rate-pill--warn";
    },
    motivoPadrao(p) {
      if (p.resolvidaPelaRegra) {
        const canc = (p.canceladasNoPhoenix || [])
          .map((c) => c.code)
          .filter(Boolean);
        return `${this.regraTexto(p.regra)}${
          canc.length ? ` · cancelada: ${canc.join(", ")}` : ""
        }`;
      }
      if (p.status === "finalizada")
        return p.operacao
          ? `Aguardando a Azul (${p.operacao})`
          : "Aguardando a Azul";
      if (p.status === "resolvida")
        return p.resolvidaEm ? `Resolvida em ${p.resolvidaEm}` : "Resolvida";
      return p.tratamento === "agente"
        ? "Na fila do agente"
        : "Correção manual";
    },
  },
};
</script>

<style src="../ImportDetail/styles.scss" scoped lang="scss" />
<style scoped lang="scss">
.detail-stats--6col {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}
.detail-stats--7 {
  grid-template-columns: repeat(7, minmax(0, 1fr));
}
@media (max-width: 1500px) {
  .detail-stats--7 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
@media (max-width: 1300px) {
  .detail-stats--6col {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 760px) {
  .detail-stats--6col {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.funnel-seg--exist {
  background: #aab2ba;
}
.funnel-seg--reimp {
  background: #5aa9a0;
}
.dot--reimp {
  background: #5aa9a0;
}
.funnel-seg--pend {
  background: #e08a1e;
}
.funnel-seg--canc {
  background: #5f6b7a;
}
.dot--canc {
  background: #5f6b7a;
}
.funnel-seg--nv {
  background: #7a5c99;
}
.dot--exist {
  background: #aab2ba;
}
.dot--pend {
  background: #e08a1e;
}
.dot--nv {
  background: #7a5c99;
}
.canc-ref {
  margin-top: 6px;
  font-size: 0.78rem;
  color: #4b535c;
  &--ok {
    color: #0f7a6e;
    font-weight: 600;
  }
}
.funnel-note {
  margin: 12px 0 0;
  font-size: 0.76rem;
  color: #8a929b;
}
.import-pend-table {
  col.col-status {
    width: 15%;
  }
  col.col-voucher {
    width: 24%;
  }
  col.col-motivo {
    width: 41%;
  }
  col.col-job {
    width: 20%;
  }
}
</style>
